import { useMemo, useState } from 'react'
import { Box, Button, Card, CardContent, Typography } from '@mui/material'
import Lightbox from 'yet-another-react-lightbox'
import Captions from 'yet-another-react-lightbox/plugins/captions'
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import Download from 'yet-another-react-lightbox/plugins/download'
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails'
import 'yet-another-react-lightbox/styles.css'
import 'yet-another-react-lightbox/plugins/captions.css'
import 'yet-another-react-lightbox/plugins/thumbnails.css'

import SectionCollapse from '../../../client/layout/SectionCollapse'
import {
  UI_COLOR_BORDER_MEDIUM,
  UI_COLOR_BORDER_MUTED,
  UI_COLOR_OVERLAY_DARK,
  UI_COLOR_PRIMARY,
  UI_COLOR_PRIMARY_ALT,
  UI_COLOR_SHADOW_CARD,
  UI_COLOR_TEXT_OVERLINE,
  UI_COLOR_TEXT_SLATE,
  UI_COLOR_TEXT_SLATE_ALT,
  UI_COLOR_WHITE
} from '../../../constants/uiColors'
import { UI_TEXT_ATTACHMENTS } from '../../../constants/uiText'
import GoogleIcon from '../../icon/GoogleIcon'
import MobileEmptyStateCard from '../../MobileEmptyStateCard'

import {
  getAttachmentLabel,
  isImageUrl,
  normalizeAttachmentList
} from '../../../helpers/paymentAttachments'

const attachmentCardSx = {
  border: `3px solid ${UI_COLOR_BORDER_MEDIUM}`,
  borderRadius: 3,
  backgroundColor: UI_COLOR_WHITE,
  boxShadow: 'none'
}

const DETAIL_SECTION_CARD_BASE_SX = {
  border: 'none !important',
  borderRadius: '10px !important',
  mb: 1.5,
  backgroundColor: `${UI_COLOR_WHITE} !important`,
  boxShadow: `${UI_COLOR_SHADOW_CARD} !important`
}

const imageThumbSx = {
  width: 74,
  height: 74,
  borderRadius: 2,
  objectFit: 'cover',
  border: `1px solid ${UI_COLOR_BORDER_MUTED}`,
  backgroundColor: UI_COLOR_WHITE,
  flexShrink: 0
}

function ImageThumbnail({ src, alt }) {
  const [hasError, setHasError] = useState(false)

  if (hasError) {
    return (
      <Box
        sx={{
          ...imageThumbSx,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <GoogleIcon name="image" size={28} color={UI_COLOR_TEXT_SLATE} />
      </Box>
    )
  }

  return (
    <Box component="img" src={src} alt={alt} sx={imageThumbSx} onError={() => setHasError(true)} />
  )
}

function OpenLinkButton({ href, label, onClick = null }) {
  const commonSx = {
    px: 0,
    minWidth: 'auto',
    fontWeight: 700,
    textTransform: 'none',
    alignSelf: 'flex-start'
  }

  if (onClick) {
    return (
      <Button
        variant="text"
        size="small"
        startIcon={<GoogleIcon name="open_in_new" size={16} />}
        sx={commonSx}
        onClick={onClick}
      >
        {label}
      </Button>
    )
  }

  return (
    <Button
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      variant="text"
      size="small"
      startIcon={<GoogleIcon name="open_in_new" size={16} />}
      sx={commonSx}
    >
      {label}
    </Button>
  )
}

/**
 * Cómo reutilizar este componente:
 *
 * <PaymentAttachmentsCard
 *   images={[{ url: imageUrl, label: 'Factura' }]}
 *   files={[{ url: fileUrl, label: 'Documento PDF' }]}
 *   title="ADJUNTO(S)"
 *   defaultOpen={false}
 *   emptyTitle="No hay adjuntos"
 *   emptyDescription="Cuando existan archivos, aparecerán aquí."
 * />
 *
 * Props:
 * - images: lista de imágenes; acepta strings, objetos { url, label } u objetos { path, label }.
 * - files: lista de archivos; también acepta strings, { url, label } o { path, label }.
 * - title: título visible del acordeón.
 * - defaultOpen: define si el acordeón inicia abierto.
 * - emptyTitle: título que se muestra cuando no hay adjuntos.
 * - emptyDescription: descripción del estado vacío.
 *
 * Si un archivo en `files` tiene extensión de imagen, se mueve al visor de imágenes.
 */
export default function PaymentAttachmentsCard({
  images = [],
  files = [],
  title = UI_TEXT_ATTACHMENTS,
  defaultOpen = false,
  emptyTitle = 'No hay adjuntos',
  emptyDescription = 'Cuando existan imágenes o archivos asociados, aparecerán aquí para su revisión.',
  sectionHeaderSx = {},
  sectionToggleSx = {}
}) {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const [showImageGallery, setShowImageGallery] = useState(false)

  const normalized = useMemo(() => {
    const normalizedImages = normalizeAttachmentList(images)
    const normalizedFiles = normalizeAttachmentList(files)

    const mergedImages = Array.from(
      [...normalizedImages, ...normalizedFiles.filter(isImageUrl)].reduce((acc, item) => {
        if (acc.some((existing) => existing.url === item.url)) return acc

        acc.push(item)
        return acc
      }, [])
    )

    const mergedFiles = Array.from(
      normalizedFiles.filter((file) => !mergedImages.some((image) => image.url === file.url))
    )

    return {
      images: mergedImages,
      files: mergedFiles
    }
  }, [files, images])

  const hasAttachments = normalized.images.length + normalized.files.length > 0

  const handleOpenImage = (index) => {
    setSelectedImageIndex(index)
    setShowImageGallery(true)
  }

  return (
    <>
      <SectionCollapse
        title={title}
        type="title"
        defaultOpen={defaultOpen}
        ariaLabelOpen="Abrir adjuntos"
        ariaLabelClose="Cerrar adjuntos"
        icon={<GoogleIcon name="attach_file" size={20} color={UI_COLOR_PRIMARY_ALT} />}
        sx={DETAIL_SECTION_CARD_BASE_SX}
        headerSx={sectionHeaderSx}
        toggleSx={sectionToggleSx}
      >
        {hasAttachments ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: 1.75
            }}
          >
            {normalized.images.map((attachment, index) => (
              <Card key={attachment.url} elevation={0} sx={attachmentCardSx}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" gap={1.5} alignItems="center">
                    <ImageThumbnail src={attachment.url} alt={`Imagen ${index + 1}`} />
                    <Box display="flex" flexDirection="column" minWidth={0} justifyContent="center">
                      <Typography
                        variant="overline"
                        color={UI_COLOR_TEXT_OVERLINE}
                        fontWeight={700}
                        sx={{ lineHeight: 1.2, letterSpacing: 0.4 }}
                      >
                        IMAGEN {index + 1}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          mb: 0.75,
                          fontSize: 15,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={getAttachmentLabel(attachment, `Imagen ${index + 1}`)}
                      >
                        {getAttachmentLabel(attachment, `Imagen ${index + 1}`)}
                      </Typography>
                      <OpenLinkButton label="Ver imagen" onClick={() => handleOpenImage(index)} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}

            {normalized.files.map((attachment, index) => (
              <Card key={attachment.url} elevation={0} sx={attachmentCardSx}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" gap={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 74,
                        height: 74,
                        borderRadius: 2,
                        border: `1px solid ${UI_COLOR_BORDER_MUTED}`,
                        backgroundColor: UI_COLOR_WHITE,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <GoogleIcon name="description" size={30} color={UI_COLOR_TEXT_SLATE_ALT} />
                    </Box>
                    <Box display="flex" flexDirection="column" minWidth={0} justifyContent="center">
                      <Typography
                        variant="overline"
                        color={UI_COLOR_TEXT_OVERLINE}
                        fontWeight={700}
                        sx={{ lineHeight: 1.2, letterSpacing: 0.4 }}
                      >
                        ARCHIVO {index + 1}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          mb: 0.75,
                          fontSize: 15,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={getAttachmentLabel(attachment, `Archivo ${index + 1}`)}
                      >
                        {getAttachmentLabel(attachment, `Archivo ${index + 1}`)}
                      </Typography>
                      <OpenLinkButton href={attachment.url} label="Abrir archivo" />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          <MobileEmptyStateCard
            icon={<GoogleIcon name="info" size={24} color={UI_COLOR_PRIMARY} />}
            title={emptyTitle}
            description={emptyDescription}
            sx={{ px: 3, py: 5 }}
          />
        )}
      </SectionCollapse>

      <Lightbox
        plugins={[Captions, Zoom, Download, Thumbnails]}
        styles={{
          container: { backgroundColor: UI_COLOR_OVERLAY_DARK },
          captionsDescriptionContainer: {
            display: 'flex',
            justifyContent: 'center'
          }
        }}
        open={showImageGallery}
        close={() => setShowImageGallery(false)}
        animation={{ swipe: 0 }}
        carousel={{ finite: true }}
        index={selectedImageIndex}
        on={{ view: ({ index }) => setSelectedImageIndex(index) }}
        zoom={{ scrollToZoom: true }}
        slides={normalized.images.map((attachment, index) => ({
          src: attachment.url,
          description: `${index + 1}/${normalized.images.length}`,
          download: { url: attachment.url, filename: `adjunto-imagen-${index + 1}` }
        }))}
      />
    </>
  )
}
